import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfig } from '@fuse/types';

import { navigation } from 'app/navigation/navigation';
import { AuthService } from '../../../main/authentication/auth.service';
import { Router } from '@angular/router';
import { ProfileService } from '../../../main/account/profile/profile.service';
import { Account } from '../../../main/account/account.model';
import { FuseUtils } from '../../../../@fuse/utils';

interface ToolbarLanguage {
    id: string;
    title: string;
    flag: string;
}

interface UserStatusOption {
    title: string;
    icon: string;
    color: string;
}

const DEFAULT_AVATAR = 'assets/images/avatars/profile.jpg';
const LANGUAGE_STORAGE_KEY = 'selected_language';
const HOMEPAGE_URL = 'https://viecnhamay.vn/';

const USER_STATUS_OPTIONS: UserStatusOption[] = [
    {
        title: 'Online',
        icon: 'icon-checkbox-marked-circle',
        color: '#4CAF50',
    },
    {
        title: 'Away',
        icon: 'icon-clock',
        color: '#FFC107',
    },
    {
        title: 'Do not Disturb',
        icon: 'icon-minus-circle',
        color: '#F44336',
    },
    {
        title: 'Invisible',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#BDBDBD',
    },
    {
        title: 'Offline',
        icon: 'icon-checkbox-blank-circle-outline',
        color: '#616161',
    },
];

const LANGUAGES: ToolbarLanguage[] = [
    {
        id: 'en',
        title: 'English',
        flag: 'us',
    },
    {
        id: 'vi',
        title: 'Tiếng Việt',
        flag: 'vi',
    },
];

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ToolbarComponent implements OnInit, OnDestroy {
    horizontalNavbar = false;
    rightNavbar = false;
    hiddenNavbar = false;
    languages: ToolbarLanguage[];
    navigation: any;
    selectedLanguage: ToolbarLanguage;
    userStatusOptions: UserStatusOption[];
    user: Account;

    // Private
    private _unsubscribeAll: Subject<void>;

    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private _authService: AuthService,
        private _profileService: ProfileService,
        private _router: Router
    ) {
        this.userStatusOptions = USER_STATUS_OPTIONS;
        this.languages = LANGUAGES;
        this.selectedLanguage = this.languages[0];

        this.navigation = navigation;

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        this.user = {
            id: FuseUtils.generateGUID(),
            avatar: DEFAULT_AVATAR,
            firstName: '',
            lastName: '',
            staffCode: '',
            phone: '',
            email: '',
            title: '',
        } as Account;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.subscribeToConfigChanges();
        this.initializeLanguage();
        this.loadUserProfile();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    toggleSidebarOpen(key: string): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    search(value: string): void {
        // Do your search here...
        console.log(value);
    }

    setLanguage(lang: ToolbarLanguage): void {
        this.selectedLanguage = lang;
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.id);
        this._translateService.use(lang.id);
        location.reload();
    }

    logout(): void {
        this._authService.logout();
        this._router.navigate(['/auth/login']);
    }

    goToHomePage() {
        location.assign(HOMEPAGE_URL);
    }

    private subscribeToConfigChanges(): void {
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings: FuseConfig) => {
                this.horizontalNavbar =
                    settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });
    }

    private initializeLanguage(): void {
        const storedLanguageId = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (storedLanguageId) {
            this._translateService.use(storedLanguageId);
        }

        const activeLanguageId =
            storedLanguageId || this._translateService.currentLang;

        this.selectedLanguage =
            _.find(this.languages, { id: activeLanguageId }) ||
            this.languages[0];
    }

    private loadUserProfile(): void {
        if (!this._authService.isAuthenticated()) {
            return;
        }

        this._profileService
            .getProfile()
            .then((res) => {
                this.user = res;
                if (this.user.avatar == null) {
                    this.user.avatar = DEFAULT_AVATAR;
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }
}
